import * as HoverCardPrimitive from "radix-ui/hover-card";
import type { ReactNode } from "react";
import { Link } from "react-router";
import type { NavFamilyItem } from "@/shared/constants/nav";
import { Icon } from "@/shared/ui/icon";

interface NavFlyoutProps {
  /** The nav row itself. It stays a link to the family's own page; the flyout is the shortcut. */
  children: ReactNode;
  items: readonly NavFamilyItem[];
  /** The family's own name, at the head of the panel. */
  label: string;
  /** Which entry is the one being read, so the flyout marks it rather than making you guess. */
  activePath: string;
  onNavigate?: (() => void) | undefined;
}

/**
 * **The six questions, one hover away** (redesign 2026-09, pass 07 decision: "Reports becomes a
 * flyout listing the six questions").
 *
 * The sidebar's Reports entry carries a chevron because it leads to a family rather than a page —
 * and a chevron that opens nothing is worse than no chevron, which is what it was until this
 * existed. Hovering or focusing the row opens the list; the row itself still goes to the hub, so
 * touch and keyboard both have a way through that does not depend on a pointer resting anywhere.
 *
 * `HoverCard` rather than a menu: this is a preview of what is behind a link, not a set of commands.
 * It opens on focus as well as hover, its contents are ordinary tabbable links, and Escape closes it
 * — none of which a `div` with `onMouseEnter` would have given for free.
 */
export function NavFlyout({ children, items, label, activePath, onNavigate }: NavFlyoutProps) {
  return (
    <HoverCardPrimitive.Root openDelay={120} closeDelay={120}>
      <HoverCardPrimitive.Trigger asChild>{children}</HoverCardPrimitive.Trigger>
      <HoverCardPrimitive.Portal>
        <HoverCardPrimitive.Content
          side="right"
          align="start"
          sideOffset={6}
          collisionPadding={8}
          className="z-50 w-72 overflow-hidden rounded-md border border-border bg-surface shadow-sm"
        >
          <p className="flex h-(--bar-h-region) items-center border-b border-border bg-surface-sunken px-3 text-caption font-semibold uppercase tracking-[0.06em] text-fg-muted">
            {label}
          </p>
          <ul className="flex flex-col p-1">
            {items.map((card) => {
              const active = activePath.startsWith(card.to.split("?")[0] ?? card.to);
              return (
                <li key={card.id}>
                  <Link
                    to={card.to}
                    onClick={onNavigate}
                    aria-current={active ? "page" : undefined}
                    className={`flex items-start gap-2.5 rounded-sm px-2 py-1.5 no-underline transition-colors duration-120 ${
                      active ? "bg-accent-soft text-accent-fg" : "text-fg hover:bg-surface-hover"
                    }`}
                  >
                    <Icon icon={card.icon} size={16} className={active ? "mt-0.5" : "mt-0.5 text-fg-muted"} />
                    <span className="min-w-0">
                      <span className="block text-cell font-medium">{card.label}</span>
                      {/* The question, not a description: it is what the reader came to ask. */}
                      <span className="block text-pretty text-caption text-fg-muted">{card.question}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Portal>
    </HoverCardPrimitive.Root>
  );
}
